import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { X, Upload, ImagePlus, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

export const CreateAuction = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.is_admin || user?.email === 'brorbyj@gmail.com';
  
  const [loading, setLoading] = useState(false);
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'other',
    starting_price: '',
    buy_now_price: '',
    duration_hours: '24',
  });

  // Redirect non-admins
  useEffect(() => {
    if (user && !isAdmin) {
      toast.error('Only admins can create auctions');
      navigate('/browse');
    }
  }, [user, isAdmin, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + imageFiles.length > 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }

    // Create previews
    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setImagePreviews([...imagePreviews, ...newPreviews]);
    setImageFiles([...imageFiles, ...files]);
  };

  const removeImage = (index) => {
    URL.revokeObjectURL(imagePreviews[index]);
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
    setImageFiles(imageFiles.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create auction (no workspace for single-admin site)
      const auctionData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        starting_price: parseFloat(formData.starting_price),
        buy_now_price: formData.buy_now_price ? parseFloat(formData.buy_now_price) : null,
        duration_hours: parseInt(formData.duration_hours),
      };

      const response = await api.post('/auctions', auctionData);
      const auctionId = response.data.auction_id;

      // Upload images if any
      if (imageFiles.length > 0) {
        const formDataImages = new FormData();
        imageFiles.forEach((file, index) => {
          formDataImages.append(`image${index}`, file);
        });

        await api.post(`/auctions/${auctionId}/images`, formDataImages, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      toast.success('Auction created successfully!');
      navigate(`/auctions/${auctionId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create auction');
    } finally {
      setLoading(false);
    }
  };

  const categories = ['watches', 'art', 'cars', 'sneakers', 'electronics', 'collectibles', 'other'];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tighter font-heading mb-2" data-testid="create-auction-heading">
              Create Auction
            </h1>
            <p className="text-lg text-muted-foreground">List your item for auction</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Images */}
            <div>
              <Label className="text-base font-medium mb-3 block">Images (Max 5)</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="relative aspect-square bg-secondary border border-border group">
                    <img src={preview} alt={`Preview ${index + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-2 right-2 bg-destructive text-white p-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-image-${index}`}
                    >
                      <X className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
                {imagePreviews.length < 5 && (
                  <label className="aspect-square bg-secondary border border-border border-dashed flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors">
                    <ImagePlus className="h-8 w-8 text-muted-foreground mb-2" strokeWidth={1.5} />
                    <span className="text-xs text-muted-foreground font-mono">Add Image</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageSelect}
                      className="hidden"
                      data-testid="image-upload-input"
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Title */}
            <div>
              <Label htmlFor="title" className="text-base font-medium mb-2 block">
                Title *
              </Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="e.g., Vintage Rolex Submariner"
                className="h-10 rounded-sm"
                data-testid="title-input"
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description" className="text-base font-medium mb-2 block">
                Description *
              </Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                placeholder="Describe your item in detail..."
                rows={5}
                className="rounded-sm resize-none"
                data-testid="description-input"
              />
            </div>

            {/* Category */}
            <div>
              <Label className="text-base font-medium mb-2 block">Category *</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                <SelectTrigger className="h-10 rounded-sm" data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="starting_price" className="text-base font-medium mb-2 block">
                  Starting Price (USD) *
                </Label>
                <Input
                  id="starting_price"
                  name="starting_price"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.starting_price}
                  onChange={handleChange}
                  required
                  placeholder="0.00"
                  className="h-10 rounded-sm font-mono"
                  data-testid="starting-price-input"
                />
              </div>

              <div>
                <Label htmlFor="buy_now_price" className="text-base font-medium mb-2 block">
                  Buy Now Price (USD) - Optional
                </Label>
                <Input
                  id="buy_now_price"
                  name="buy_now_price"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.buy_now_price}
                  onChange={handleChange}
                  placeholder="0.00"
                  className="h-10 rounded-sm font-mono"
                  data-testid="buy-now-price-input"
                />
              </div>
            </div>

            {/* Duration */}
            <div>
              <Label className="text-base font-medium mb-2 block">Auction Duration *</Label>
              <Select value={formData.duration_hours} onValueChange={(value) => setFormData({ ...formData, duration_hours: value })}>
                <SelectTrigger className="h-10 rounded-sm" data-testid="duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24">24 Hours</SelectItem>
                  <SelectItem value="48">48 Hours</SelectItem>
                  <SelectItem value="72">3 Days</SelectItem>
                  <SelectItem value="168">7 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Submit */}
            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="h-12 px-8 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-medium"
                data-testid="submit-auction-button"
              >
                {loading ? 'Creating...' : 'Create Auction'}
              </Button>
              <Button
                type="button"
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="h-12 px-8 rounded-sm font-medium border-2"
                data-testid="cancel-button"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
